// BookingsPage — two-tab view at /bookings.
//
// Tab 1 "Today's Diary": driver-grouped operational view of today's bookings.
//   Uses useTodayDiary (fetches /bookings filtered to today; no per-driver
//   /diary call needed).
//
// Tab 2 "Bookings": flat date-filterable list of all bookings.
//   Date defaults to today; driver filter is optional.
//
// Decision 3.12: /bookings is the two-tab page, not a single-mode list.

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, ClipboardList, CalendarDays } from 'lucide-react';
import { Card } from '../../components/ui/Card/Card';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { EmptyState } from '../../components/ui/EmptyState/EmptyState';
import { FilterBar } from '../../components/ui/FilterBar/FilterBar';
import { Table, type Column } from '../../components/ui/Table/Table';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import { NotifiedBadge } from '../../components/ui/NotifiedBadge/NotifiedBadge';
import { Tooltip } from '../../components/ui/Tooltip/Tooltip';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import { Input } from '../../components/form/Input';
import { cn } from '../../utils/cn';
import { useDiaryDrivers } from '../diary/hooks/useDiaryDrivers';
import { useTodayDiary, todayISO } from '../diary/hooks/useTodayDiary';
import { useCreatedBookings } from './hooks/useCreatedBookings';
import type { BookingStatus, BookingSummary } from '../../types/api';
import styles from './BookingsPage.module.css';

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

const NOTE_TRUNCATE_AT = 60;

// Task 6B — `cancellation_requested` is amber/warning so staff browsing
// the bookings list can spot pending customer requests at a glance.
// The 'danger' red is reserved for terminal `cancelled`.
const BOOKING_STATUS_VARIANTS: Record<BookingStatus, BadgeVariant> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancellation_requested: 'warning',
  cancelled: 'muted',
};

type StatusFilter = 'all' | 'in_progress' | 'pending' | 'completed';

const STATUS_FILTER_PREDICATE: Record<StatusFilter, (b: BookingSummary) => boolean> = {
  all: () => true,
  in_progress: (b) => b.status === 'in_progress',
  pending: (b) => b.status === 'pending',
  completed: (b) => b.status === 'completed',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function truncate(text: string, n: number): string {
  return text.length <= n ? text : `${text.slice(0, n - 1).trimEnd()}…`;
}

function formatDateLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

interface DriverGroup {
  driverId: number | null;
  driverName: string;
  bookings: BookingSummary[];
}

function groupByDriver(rows: BookingSummary[]): DriverGroup[] {
  const map = new Map<string, DriverGroup>();
  for (const b of rows) {
    const key =
      b.driver_id !== null
        ? `id:${b.driver_id}`
        : `name:${b.driver_name ?? 'unassigned'}`;
    const name = b.driver_name ?? 'Unassigned';
    let group = map.get(key);
    if (!group) {
      group = { driverId: b.driver_id, driverName: name, bookings: [] };
      map.set(key, group);
    }
    group.bookings.push(b);
  }
  return Array.from(map.values()).sort((a, b) => {
    if (a.driverName === 'Unassigned') return 1;
    if (b.driverName === 'Unassigned') return -1;
    return a.driverName.localeCompare(b.driverName, undefined, { sensitivity: 'base' });
  });
}

// ---------------------------------------------------------------------------
// Shared columns (diary + list tabs share most columns)
// ---------------------------------------------------------------------------

function buildBookingColumns(includeDriver: boolean): Column<BookingSummary>[] {
  const cols: Column<BookingSummary>[] = [
    {
      key: 'timeframe',
      header: 'Time',
      width: '90px',
      render: (_v, row) => <span className={styles.timeCell}>{row.timeframe}</span>,
    },
    { key: 'name', header: 'Customer' },
    {
      key: 'address',
      header: 'Address',
      render: (_v, row) =>
        `${row.effective_address.address}, ${row.effective_address.postcode}`,
    },
    {
      key: 'car_make_model',
      header: 'Vehicle',
      render: (_v, row) => `${row.car_make_model} (${row.car_registration})`,
    },
    {
      key: 'status',
      header: 'Status',
      width: '160px',
      render: (_v, row) => (
        <StatusBadge status={row.status} statusMap={BOOKING_STATUS_VARIANTS} />
      ),
    },
    {
      key: 'email_reminder_sent',
      header: 'Notified',
      width: '130px',
      render: (_v, row) => (
        <NotifiedBadge
          emailSent={row.email_reminder_sent ?? false}
          smsSent={row.sms_reminder_sent ?? false}
          emailSentAt={row.email_reminder_sent_at ?? null}
          smsSentAt={row.sms_reminder_sent_at ?? null}
        />
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (_v, row) => {
        const note = row.notes ?? '';
        if (note.length === 0)
          return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
        const shown = truncate(note, NOTE_TRUNCATE_AT);
        if (note.length <= NOTE_TRUNCATE_AT) return <span>{shown}</span>;
        return (
          <Tooltip content={note}>
            <span className={styles.notesCell} title={note}>
              {shown}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: 'enc_id',
      header: '',
      width: '70px',
      render: (_v, row) => (
        <Link to={`/collections/bookings/${row.enc_id}`} className={styles.linkCell}>
          View
        </Link>
      ),
    },
  ];

  if (includeDriver) {
    cols.splice(1, 0, {
      key: 'driver_name',
      header: 'Driver',
      render: (_v, row) => row.driver_name ?? '—',
    });
  }

  return cols;
}

// ---------------------------------------------------------------------------
// Tab 1 — Today's Diary (driver-grouped)
// ---------------------------------------------------------------------------

interface DiaryTabProps {
  date: string;
}

function DiaryTab({ date }: DiaryTabProps): JSX.Element {
  const [driverFilter, setDriverFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const driversQuery = useDiaryDrivers();
  const driverIdFilter = driverFilter === 'all' ? null : Number(driverFilter);
  const todayQuery = useTodayDiary({ date, driverId: driverIdFilter });

  const driverOptions = useMemo(
    () => [
      { value: 'all', label: 'All Drivers' },
      ...(driversQuery.data ?? []).map((d) => ({
        value: String(d.id),
        label: d.name,
      })),
    ],
    [driversQuery.data],
  );

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'in_progress', label: 'Confirmed' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Done' },
  ];

  const filteredBookings = useMemo(
    () => (todayQuery.data ?? []).filter(STATUS_FILTER_PREDICATE[statusFilter]),
    [todayQuery.data, statusFilter],
  );

  const groups = useMemo(() => groupByDriver(filteredBookings), [filteredBookings]);
  const columns = useMemo(() => buildBookingColumns(false), []);

  const totalRows = todayQuery.data?.length ?? 0;

  return (
    <>
      <FilterBar ariaLabel="Diary filters">
        <Select
          aria-label="Driver"
          value={driverFilter}
          onChange={(e) => setDriverFilter(e.target.value)}
          options={driverOptions}
        />
        <Select
          aria-label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          options={statusOptions}
        />
      </FilterBar>

      {todayQuery.isLoading ? (
        <Card>
          <Table<BookingSummary>
            columns={columns}
            data={[]}
            keyField={(r) => r.uuid}
            loading
          />
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={ClipboardList}
            title={`No bookings scheduled for ${formatDateLong(date)}.`}
            description="Once a job is booked it will appear here grouped by driver."
            action={
              <Link to="/collections/bookings/create" style={{ textDecoration: 'none' }}>
                <Button variant="primary" icon={Plus}>
                  Book a job
                </Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div
          className={styles.driverCards}
          aria-label={`${totalRows} booking${totalRows === 1 ? '' : 's'} across ${groups.length} driver${groups.length === 1 ? '' : 's'}`}
        >
          {groups.map((group) => (
            <Card
              key={group.driverName}
              title={
                <div className={styles.driverCardTitle}>
                  <span>{group.driverName}</span>
                  <span className={styles.bookingCount}>
                    {group.bookings.length}
                    {group.bookings.length === 1 ? ' booking' : ' bookings'}
                  </span>
                </div>
              }
              flush
            >
              <div className={styles.tableWrap}>
                <Table<BookingSummary>
                  columns={columns}
                  data={group.bookings}
                  keyField={(r) => r.uuid}
                  ariaLabel={`Bookings for ${group.driverName}`}
                />
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Bookings list (flat, date-filterable)
// ---------------------------------------------------------------------------

function BookingsListTab(): JSX.Element {
  const [date, setDate] = useState(todayISO());
  const [driverFilter, setDriverFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const driversQuery = useDiaryDrivers();
  const driverIdFilter = driverFilter === 'all' ? null : Number(driverFilter);
  const query = useTodayDiary({ date, driverId: driverIdFilter });

  const driverOptions = useMemo(
    () => [
      { value: 'all', label: 'All Drivers' },
      ...(driversQuery.data ?? []).map((d) => ({
        value: String(d.id),
        label: d.name,
      })),
    ],
    [driversQuery.data],
  );

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'in_progress', label: 'Confirmed' },
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Done' },
  ];

  const filtered = useMemo(
    () => (query.data ?? []).filter(STATUS_FILTER_PREDICATE[statusFilter]),
    [query.data, statusFilter],
  );

  const columns = useMemo(() => buildBookingColumns(true), []);

  return (
    <>
      <FilterBar ariaLabel="Bookings filters">
        <div className={styles.listControls}>
          <DatePicker
            label="Date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <Select
            aria-label="Driver"
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            options={driverOptions}
          />
          <Select
            aria-label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            options={statusOptions}
          />
        </div>
      </FilterBar>

      <Card>
        <Table<BookingSummary>
          columns={columns}
          data={filtered}
          keyField={(r) => r.uuid}
          loading={query.isLoading}
          ariaLabel="Bookings list"
          emptyState={<span>{`No bookings found for ${formatDateLong(date)}.`}</span>}
        />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Created today (legacy daily_bookings.php)
// ---------------------------------------------------------------------------

function formatCreated(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return `${dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })} ${dt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}

function buildCreatedColumns(): Column<BookingSummary>[] {
  return [
    {
      key: 'created_at',
      header: 'Created',
      width: '170px',
      render: (_v, row) => <span data-testid="created-at">{formatCreated(row.created_at)}</span>,
    },
    { key: 'name', header: 'Customer' },
    {
      key: 'car_make_model',
      header: 'Vehicle',
      render: (_v, row) => `${row.car_make_model} (${row.car_registration})`,
    },
    {
      key: 'date',
      header: 'Booked for',
      render: (_v, row) => `${row.date} ${row.timeframe}`,
    },
    {
      key: 'driver_name',
      header: 'Driver',
      render: (_v, row) => row.driver_name ?? '—',
    },
    {
      key: 'status',
      header: 'Status',
      width: '160px',
      render: (_v, row) => (
        <StatusBadge status={row.status} statusMap={BOOKING_STATUS_VARIANTS} />
      ),
    },
    {
      key: 'enc_id',
      header: '',
      width: '70px',
      render: (_v, row) => (
        <Link to={`/collections/bookings/${row.enc_id}`} className={styles.linkCell}>
          View
        </Link>
      ),
    },
  ];
}

function CreatedTodayTab(): JSX.Element {
  const [date, setDate] = useState(todayISO());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Light debounce so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useCreatedBookings({ date, q: search });
  const rows = query.data?.bookings ?? [];
  const columns = useMemo(() => buildCreatedColumns(), []);
  const searching = search !== '';

  return (
    <>
      <FilterBar ariaLabel="Created bookings filters">
        <div className={styles.listControls}>
          <DatePicker
            label="Created on"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={searching}
          />
          <Input
            aria-label="Search all bookings"
            placeholder="Search all bookings (name, reg, ref, phone, postcode)…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </FilterBar>

      <Card>
        <p className={styles.createdSummary} aria-live="polite">
          {searching
            ? `${query.data?.total ?? 0} result${(query.data?.total ?? 0) === 1 ? '' : 's'} across all bookings`
            : `${query.data?.total ?? 0} booking${(query.data?.total ?? 0) === 1 ? '' : 's'} created on ${formatDateLong(date)}`}
        </p>
        <Table<BookingSummary>
          columns={columns}
          data={rows}
          keyField={(r) => r.uuid}
          loading={query.isLoading}
          ariaLabel="Created bookings"
          emptyState={
            <span>
              {searching
                ? 'No bookings match your search.'
                : `No bookings were created on ${formatDateLong(date)}.`}
            </span>
          }
        />
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// BookingsPage
// ---------------------------------------------------------------------------

type TabId = 'diary' | 'list' | 'created';

const TABS: { id: TabId; label: string }[] = [
  { id: 'diary', label: "Today's Diary" },
  { id: 'list', label: 'Bookings' },
  { id: 'created', label: 'Created Today' },
];

export function BookingsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabId | null;
  const activeTab: TabId =
    tabParam === 'list' ? 'list' : tabParam === 'created' ? 'created' : 'diary';
  const today = useMemo(() => todayISO(), []);

  function switchTab(id: TabId) {
    if (id === 'diary') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: id }, { replace: true });
    }
  }

  const totalDiaryQuery = useTodayDiary({ date: today });
  const totalCount = totalDiaryQuery.data?.length ?? 0;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Bookings"
        subtitle={`${formatDateLong(today)} · ${totalCount} booking${totalCount === 1 ? '' : 's'} today`}
        actions={
          <>
            <Link to="/collections/bookings/create" style={{ textDecoration: 'none' }}>
              <Button variant="primary" icon={Plus}>
                Book In
              </Button>
            </Link>
            <Link to="/collections/diary" style={{ textDecoration: 'none' }}>
              <Button variant="secondary" icon={CalendarDays}>
                Diary
              </Button>
            </Link>
          </>
        }
      />

      <nav className={styles.tabStrip} role="tablist" aria-label="Bookings tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`bookings-panel-${tab.id}`}
            id={`bookings-tab-${tab.id}`}
            className={cn(styles.tabBtn, activeTab === tab.id && styles.tabBtnActive)}
            onClick={() => switchTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div
        id={`bookings-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`bookings-tab-${activeTab}`}
      >
        {activeTab === 'diary' ? (
          <DiaryTab date={today} />
        ) : activeTab === 'list' ? (
          <BookingsListTab />
        ) : (
          <CreatedTodayTab />
        )}
      </div>
    </div>
  );
}
